import { Component, Inject, OnInit, Optional, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { ConfirmDialogComponent } from "src/app/material-module/confirm-dialog/confirm-dialog.component";
import { User } from "src/app/models/user";
import { ListPropertyPipe } from "src/app/pipe/list-property.pipe";
import { UserService } from "src/app/services/user.service";
import { AuthService } from "src/app/services/auth.service";
import { dataTableColumnDefinition } from "src/app/shared/resourceful-index/dataTableColumnDefinition";
import { ResourcefulIndexComponent } from "src/app/shared/resourceful-index/resourceful-index/resourceful-index.component";
import { ComuniComponent } from "../comuni/comuni.component";
import { EditComponent } from "./dialogs/edit/edit.component";

type PreferenceHintItem = {
  userId: number;
  user?: User | null;
  label: string;
};

@Component({
  selector: "users-component",
  styleUrls: ["users.component.less"],
  templateUrl: "users.component.html",
  providers: [ListPropertyPipe],
})
export class UsersComponent implements OnInit {
  headingText: string = "Utenti";
  preferenceHints: { [userId: number]: string | string[] } = {};
  visiblePreferenceHints: PreferenceHintItem[] = [];
  private preferenceHintUsersCache: { [userId: number]: User | null } = {};
  private loadingPreferenceHintUsers = new Set<number>();

  relations: string[] = [
    "ruoli",
    "studio",
    "comune",
    "comune.provincia",
    "filiale",
    "linkedUsers.studio",
  ];

  @ViewChild("index") index: ResourcefulIndexComponent;

  selectionMode: boolean = false;

  loading: boolean = true;

  search: any = {};
  rowClassGetter = (model: User) =>
    this.hasPreferenceHints() && model?.id && this.preferenceHints[model.id]
      ? "preferred-user"
      : "";

  canEditUsers: boolean = false;
  allStudios: boolean = false;

  columnDefinitions: dataTableColumnDefinition[] = [
    new dataTableColumnDefinition({
      humanName: "ID",
      internalName: "id",
    }),
    new dataTableColumnDefinition({
      humanName: "Qualifica",
      internalName: "qualifica",
    }),
    new dataTableColumnDefinition({
      humanName: "Titolo",
      internalName: "titolo",
    }),
    new dataTableColumnDefinition({
      humanName: "Nome",
      internalName: "humanIdentifier",
      sortDisabled: true,
    }),
    new dataTableColumnDefinition({
      humanName: "E-mail",
      internalName: "email",
    }),
    new dataTableColumnDefinition({
      humanName: "Ruoli",
      internalName: "ruoli",
      sortDisabled: true,
      displayFunction: (model) =>
        this.listPropertyPipe.transform(model.ruoli).substring(0, 25),
    }),
    new dataTableColumnDefinition({
      humanName: "Studio",
      internalName: "studio",
      sortDisabled: true,
      displayFunction: (model) => model.studio?.nome,
    }),
    new dataTableColumnDefinition({
      humanName: "Filiale",
      internalName: "filiale",
      displayFunction: (model) => model.filiale?.nome,
    }),
    new dataTableColumnDefinition({
      humanName: "Indirizzo",
      internalName: "fullAddress",
      sortDisabled: true,
    }),

    new dataTableColumnDefinition({
      humanName: "Telefono",
      internalName: "tel",
    }),
    new dataTableColumnDefinition({
      humanName: "Cellulare",
      internalName: "cell",
    }),

    new dataTableColumnDefinition({
      humanName: "Fax",
      internalName: "fax",
    }),
  ];

  constructor(
    public userService: UserService,
    public dialog: MatDialog,
    private toast: ToastrService,
    private authService: AuthService,
    @Optional() private dialogRef: MatDialogRef<ComuniComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data,
    public listPropertyPipe: ListPropertyPipe
  ) {
    if (this.data?.selectionMode) {
      this.selectionMode = true;
      this.headingText = "Seleziona un utente";
    }

    if (this.data?.defaultSearch) {
      this.search = this.data.defaultSearch;
    }

    if (this.data?.preferenceHints) {
      this.preferenceHints = this.data.preferenceHints;
    }

    if (this.data?.allStudios) {
      this.allStudios = true;
    }
  }

  /** Override function per caricare utenti da tutti gli studi */
  allStudiosOverrideFunction = async (search, paginationInfo) => {
    return this.userService.getAll(this.relations, paginationInfo, {
      extraParams: { all_studios: 'true' }
    });
  };

  form: FormGroup;

  async ngOnInit() {
    // compute authorization to edit users: ruoli id 1 or 18
    try {
      const logged = await this.authService.loggedInUser.getValue();
      this.canEditUsers =
        Array.isArray(logged?.ruoli) &&
        logged.ruoli.some((r) => [1, 18].includes(r.id));
    } catch {}
    this.loading = false;
  }

  closeDialogWithSelectedModel(model) {
    this.dialogRef?.close(model);
  }

  async updateDataSource() {
    await this.index?.updateData();
  }

  deleteFunction = async (user: User) => {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `Sei sicuro di voler eliminare l'utente ${user.humanIdentifier}?`,
      },
    });
    dialogRef.afterClosed().subscribe(async (data) => {
      if (!data) {
        this.toast.success("Account non eliminato.");
      }
      if (data) {
        try {
          await this.userService.delete(user);
          this.toast.success("Account eliminato.");
        } catch (e) {
          console.error(e);
          this.toast.error("Eliminazione Account fallita.", "Errore!");
        } finally {
          this.updateDataSource();
        }
      }
    });
  };

  add() {
    let addDialogRef = this.dialog.open(EditComponent, { data: this.search });
    addDialogRef.afterClosed().subscribe(async (data) => {
      if (data) {
        let user = new User().deserialize(data);

        try {
          await this.userService.create(user);
          this.toast.success("Account creato.");
        } catch (e) {
          console.error(e);
          this.toast.error("Creazione account fallita.", "Errore!");
        } finally {
          this.updateDataSource();
        }
      }
    });
  }

  editFunction = async (user: User) => {
    let editDialogRef = this.dialog.open(EditComponent, { data: user });

    editDialogRef.afterClosed().subscribe(async (data) => {
      if (data) {
        try {
          Object.assign(user, data);
          await this.userService.edit(user);
          this.toast.success("Account modificato.");

          // Aggiorna la sessione dell'utente loggato (per relazione simmetrica linked_users)
          if (data.linked_user_ids !== undefined) {
            const loggedUser = this.authService.loggedInUser.getValue();
            if (loggedUser?.id) {
              const updatedLoggedUser = await this.userService.getOne(loggedUser.id, this.relations);
              this.authService.updateLoggedInUser(updatedLoggedUser);
            }
          }
        } catch (e) {
          console.error(e);
          this.toast.error("Modifica account fallita.", "Errore!");
        } finally {
          this.updateDataSource();
        }
      }
    });
  };

  private hasPreferenceHints(): boolean {
    return (
      !!this.preferenceHints &&
      Object.keys(this.preferenceHints || {}).length > 0
    );
  }

  formatPreferenceHint(userId?: number): string {
    if (!userId) return "";
    const hint = this.preferenceHints[userId];
    if (!hint) return "";
    if (Array.isArray(hint)) {
      return hint.join(" | ");
    }
    return hint;
  }

  updateVisiblePreferenceHints(): void {
    if (!this.hasPreferenceHints()) {
      this.visiblePreferenceHints = [];
      return;
    }

    const result: PreferenceHintItem[] = [];
    const models: User[] = (this.index?.models as User[]) || [];
    const previousHints = this.visiblePreferenceHints || [];
    for (const userIdStr of Object.keys(this.preferenceHints)) {
      const userId = +userIdStr;
      const userFromModels = models.find((m) => m?.id === userId);
      if (userFromModels?.id) {
        this.preferenceHintUsersCache[userId] = userFromModels;
      }

      const previousUser =
        previousHints.find((hint) => hint.userId === userId)?.user || null;
      const cachedUser =
        userFromModels ||
        this.preferenceHintUsersCache[userId] ||
        previousUser ||
        null;

      if (!cachedUser && !(userId in this.preferenceHintUsersCache)) {
        this.preloadPreferenceHintUser(userId);
      }

      const name = cachedUser?.humanIdentifier || `Utente #${userId}`;
      result.push({
        userId,
        user: cachedUser,
        label: `${name}: ${this.formatPreferenceHint(userId)}`,
      });
    }

    this.visiblePreferenceHints = result;
  }

  private async preloadPreferenceHintUser(userId: number): Promise<void> {
    if (this.loadingPreferenceHintUsers.has(userId)) {
      return;
    }

    this.loadingPreferenceHintUsers.add(userId);
    try {
      const user = await this.userService.getOne(userId, this.relations);
      this.preferenceHintUsersCache[userId] = user;
    } catch (error) {
      console.error("Impossibile caricare l'utente preferito", error);
      this.preferenceHintUsersCache[userId] = null;
    } finally {
      this.loadingPreferenceHintUsers.delete(userId);
      this.updateVisiblePreferenceHints();
    }
  }

  async selectPreferenceHint(hint: PreferenceHintItem): Promise<void> {
    if (!this.selectionMode || !hint?.userId) {
      return;
    }

    let userToSelect =
      hint.user ||
      (this.index?.models as User[] | undefined)?.find(
        (m) => m?.id === hint.userId
      );

    if (!userToSelect) {
      try {
        userToSelect = await this.userService.getOne(
          hint.userId,
          this.relations
        );
        hint.user = userToSelect;
      } catch (error) {
        console.error("Impossibile caricare l'utente preferito", error);
        this.toast.error(
          "Non riesco a caricare l'utente selezionato.",
          "Errore"
        );
        return;
      }
    }

    this.closeDialogWithSelectedModel(userToSelect);
  }
}
